/**
 * LiveTestingBodySelectTreeModal
 *
 * body_select (트리 기반) 타입 질문에서 BODY PART 버튼 클릭 시 열리는 모달
 * V2 전용 구현 (V1 ModalBodySelectTree의 V2 버전)
 *
 * 기능:
 * - body_select_whole SVG 선택 UI 제공
 * - WHOLE BODY, OTHER 버튼 제공
 * - OTHER 선택 시 description 입력 가능
 * - 선택된 body part 표시
 * - 저장/취소 기능
 */

import { isNotEmpty } from '@cyren/common-lib';
import { Button, InputAdornment, TextField } from '@mui/material';
import { last, truncate, upperCase } from 'lodash';
import { memo, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { BsFillPlusCircleFill, BsPlusCircle } from 'react-icons/bs';
import { FaArrowLeft } from 'react-icons/fa';
import { FaArrowsUpDown, FaRotate } from 'react-icons/fa6';
import { MdClose } from 'react-icons/md';
import { useSetRecoilState } from 'recoil';
import { useStaticLocales, type SystemLocaleCodes } from '../../../../../admin/hooks/locales/use-static-locale';
import { getLocaleCode } from '../../../organization/configurations/language/constants';
import { useTranslations } from '../../../../../patient/hooks/use-translations';
import { translationControllerTransForQuestion } from '../../../../api/generated/translation/translation';
import type { BodySelectTreeConfigResponseDto, QuestionResponseDto } from '../../../../api/generated/types';
import type { LiveTestingAnswerValue } from './types';
import LiveTestingQuestionFieldInner from './LiveTestingQuestionFieldInner';
import { PaDataRepoState } from '../../../../../report/use-data';
import { normalizeQuestionLabelForSurveyLocale } from '../../../../shared/utils/survey-locale-text';

// V1 SVG 버튼 컴포넌트 재사용 - Main body
import BackFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BackFemaleButtons';
import BodyFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BodyFemaleButtons';
import BodyMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BodyMaleButtons';
import BodyRotateMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BodyRotateMaleButtons';

// V1 SVG 버튼 컴포넌트 재사용 - Subparts
import BackLowerFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BackLowerFemaleButtons';
import BackUpperFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BackUpperFemaleButtons';
import BellyFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BellyFemaleButtons';
import BellyMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BellyMaleButtons';
import BottomFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BottomFemaleButtons';
import BottomMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BottomMaleButtons';
import FaceFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/FaceFemaleButtons';
import FaceMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/FaceMaleButtons';
import FootLeftButtons from '../../../../../patient/question/typed-qs/body-select/buttons/FootLeftButtons';
import FootRightButtons from '../../../../../patient/question/typed-qs/body-select/buttons/FootRightButtons';
import HandLeftButtons from '../../../../../patient/question/typed-qs/body-select/buttons/HandLeftButtons';
import HandRightButtons from '../../../../../patient/question/typed-qs/body-select/buttons/HandRightButtons';
import LeftArmFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/LeftArmFemaleButtons';
import LeftArmMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/LeftArmMaleButtons';
import LegLeftButtons from '../../../../../patient/question/typed-qs/body-select/buttons/LegLeftButtons';
import LegLeftFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/LegLeftFemaleButtons';
import LegRightButtons from '../../../../../patient/question/typed-qs/body-select/buttons/LegRightButtons';
import LegRightFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/LegRightFemaleButtons';
import MouthButtons from '../../../../../patient/question/typed-qs/body-select/buttons/MouthButtons';
import MouthLiftButtons from '../../../../../patient/question/typed-qs/body-select/buttons/MouthLiftButtons';
import RightArmFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/RightArmFemaleButtons';
import RightArmMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/RightArmMaleButtons';
import WholeBodyBackLowerMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/WholeBodyBackLowerMaleButtons';
import WholeBodyBackUpperMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/WholeBodyBackUpperMaleButtons';
import WholeChestFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/WholeChestFemaleButtons';
import WholeChestMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/WholeChestMaleButtons';

type SubpartKey = string;

interface SubpartMainOption {
  key: string;
  requireNote?: boolean;
}

interface CheckboxOption {
  key: string;
  requireNote?: boolean;
}

const CHECKBOX_SUBPART_KEYS = new Set(['o-head', 'o-neck']);

const normalizeSubpartLabel = (rawLabel?: string) => {
  if (typeof rawLabel !== 'string') return undefined;

  const trimmed = rawLabel.trim();
  if (!trimmed) return undefined;

  // Ex) "Q-전신-머리" -> "머리", "Q-whole-body-head" -> "head"
  const withoutQuestionPrefix = trimmed.replace(/^[Qq][-_\\s]*/, '');
  const segments = withoutQuestionPrefix
    .split('-')
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (segments.length >= 2) {
    return segments[segments.length - 1];
  }

  return withoutQuestionPrefix;
};

const getDirectionalLabelKey = (optionKey: string) => {
  if (optionKey.endsWith('-rt') || optionKey.endsWith('-right')) {
    return 'label-right';
  }
  if (optionKey.endsWith('-lt') || optionKey.endsWith('-left')) {
    return 'label-left';
  }
  return null;
};

interface LiveTestingBodySelectTreeModalProps {
  /** 모달 열림 여부 */
  isOpen: boolean;
  /** 모달 제목 */
  title?: string;
  /** 모달 부제목 */
  subtitle?: string;
  /** 초기 선택된 키 배열 */
  initialSelectedKeys?: string[];
  /** 초기 notesForKeys (key별 description) */
  initialNotesForKeys?: Record<string, string>;
  /** 초기 follow-up 질문 답변 맵 */
  initialAnswerMap?: Record<string, LiveTestingAnswerValue>;
  /** 남성/여성 이미지 (기본값: true = 남성) */
  isMale?: boolean;
  /** 저장 핸들러 */
  onSave: (
    selectedKeys: string[],
    notesForKeys: Record<string, string>,
    answerMap: Record<string, LiveTestingAnswerValue>
  ) => void;
  /** 닫기 핸들러 */
  onClose: () => void;
  /** 저장 버튼 텍스트 */
  saveButtonText?: string;
  /** 번역 locale (V1과 동일하게 useTranslations에 전달) */
  locale?: string;
  /** Survey ID (subpart 클릭 시 해당 question의 번역을 로드하기 위해 필요) */
  surveyId?: string | number;
  /** body_select 트리 키 */
  treeKey?: string;
  /** body_select tree config (entry question + subpart questions) */
  treeConfig?: BodySelectTreeConfigResponseDto;
  /** body_select tree config 로딩 여부 */
  isTreeConfigLoading?: boolean;
  /** body_select tree follow-up questions (entry question 제외) */
  followUpQuestions?: QuestionResponseDto[];
  /** tree lookup + question lookup 기준 readiness */
  isTreeReady?: boolean;
}

// API 응답 타입 정의
interface TranslationApiResponse {
  id?: number;
  key: string;
  value?: string;
  locale?: string;
  type?: string;
  dataType?: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
  data?: {
    translations?: TranslationApiResponse[];
  };
  timestamp: string;
}

function LiveTestingBodySelectTreeModal({
  isOpen,
  title,
  subtitle,
  initialSelectedKeys = [],
  initialNotesForKeys = {},
  initialAnswerMap = {},
  isMale = true,
  onSave,
  onClose,
  saveButtonText,
  locale,
  surveyId,
  treeKey,
  treeConfig,
  isTreeConfigLoading = false,
  followUpQuestions = [],
  isTreeReady = false,
}: LiveTestingBodySelectTreeModalProps) {
  // DEBUG: treeConfig 확인용 로그
  console.log('[BodySelectTreeModal] props:', {
    isOpen,
    surveyId,
    treeKey,
    treeConfig,
    isTreeConfigLoading,
    hasEntryQuestion: !!treeConfig?.entryQuestion,
    isMale,
  });

  const localeCode = getLocaleCode(locale || 'english') as SystemLocaleCodes;
  const { sltStr } = useStaticLocales({ localeCode });
  const [, { t }] = useTranslations({ locale });
  const setDataState = useSetRecoilState(PaDataRepoState);

  // 이미 로드한 subpart question 키를 추적 (중복 로드 방지)
  const loadedQuestionKeysRef = useRef<Set<string>>(new Set());

  // 현재 스텝 상태 (V1: 부위 선택 후 Next -> follow-up 질문 표시)
  const [step, setStep] = useState<'body-select' | 'follow-ups'>('body-select');

  // 선택된 키 상태
  const [selectedKeys, setSelectedKeys] = useState<string[]>(initialSelectedKeys);
  // Key별 description 상태
  const [notesForKeys, setNotesForKeys] = useState<Record<string, string>>(initialNotesForKeys);
  // follow-up 질문 답변 상태
  const [answerMap, setAnswerMap] = useState<Record<string, LiveTestingAnswerValue>>(initialAnswerMap);
  // 현재 description 입력 중인 키 (OTHER 등)
  const [requireNoteKey, setRequireNoteKey] = useState<string | null>(null);
  // 앞면/뒷면 토글 상태
  const [rotate, setRotate] = useState(false);
  // 하위 화면 네비게이션 스택
  const [selectedStack, setSelectedStack] = useState<string[]>([]);
  // Mouth - tongue lift 토글 상태
  const [tongueLift, setTongueLift] = useState(false);

  // 현재 하위 화면 키
  const currentSubpart = last(selectedStack) as SubpartKey | undefined;

  const optionToQuestionKeyMap = useMemo(() => {
    const map = new Map<string, string>();
    treeConfig?.subparts?.forEach((subpart) => {
      if (subpart?.optionKey && subpart?.questionKey) {
        map.set(subpart.optionKey, subpart.questionKey);
      }
    });
    return map;
  }, [treeConfig]);

  const subpartKeys = useMemo(() => Array.from(optionToQuestionKeyMap.keys()), [optionToQuestionKeyMap]);

  const isSubpartKey = useCallback(
    (key: string): key is SubpartKey => optionToQuestionKeyMap.has(key),
    [optionToQuestionKeyMap]
  );

  const mainOptions = useMemo<SubpartMainOption[]>(() => {
    const entryQuestion = treeConfig?.entryQuestion;
    if (!entryQuestion?.answerKeys?.length) return [];

    const optionsMap = (entryQuestion.answerOptionsMap ?? {}) as Record<string, any>;
    return entryQuestion.answerKeys
      .filter((key) => optionsMap[key]?.mainOption)
      .map((key) => ({
        key,
        requireNote: optionsMap[key]?.requireNote,
      }));
  }, [treeConfig]);

  const subpartMainOptionsMap = useMemo<Record<string, SubpartMainOption[]>>(() => {
    const map: Record<string, SubpartMainOption[]> = {};
    treeConfig?.subparts?.forEach((subpart) => {
      const question = subpart?.question;
      const answerKeys = question?.answerKeys ?? [];
      const optionsMap = (question?.answerOptionsMap ?? {}) as Record<string, any>;

      map[subpart.optionKey] = answerKeys
        .filter((key) => optionsMap[key]?.mainOption)
        .map((key) => ({
          key,
          requireNote: optionsMap[key]?.requireNote,
        }));
    });
    return map;
  }, [treeConfig]);

  const subpartCheckboxOptionsMap = useMemo<Record<string, CheckboxOption[]>>(() => {
    const map: Record<string, CheckboxOption[]> = {};
    treeConfig?.subparts?.forEach((subpart) => {
      if (!CHECKBOX_SUBPART_KEYS.has(subpart.optionKey)) {
        return;
      }
      const question = subpart?.question;
      const answerKeys = question?.answerKeys ?? [];
      const optionsMap = (question?.answerOptionsMap ?? {}) as Record<string, any>;

      map[subpart.optionKey] = answerKeys.map((key) => ({
        key,
        requireNote: optionsMap[key]?.requireNote,
      }));
    });
    return map;
  }, [treeConfig]);
  const hasFollowUps = followUpQuestions.length > 0;

  // treeConfig.entryQuestion이 있으면 config 기반으로 동작 가능 (treeInfo 없이도)
  const isConfigReady = !!treeConfig?.entryQuestion;
  const shouldBlockSelection = (isTreeConfigLoading || !isTreeReady) && !isConfigReady;
  const showMissingTree = !isTreeConfigLoading && !isTreeReady && !isConfigReady;
  const showLoading = isTreeConfigLoading && !isConfigReady;
  const canProceed = isTreeReady || isConfigReady;

  // 이전 isOpen 상태 추적 (모달이 열릴 때만 초기화하기 위해)
  const prevIsOpenRef = useRef<boolean>(false);

  // 모달이 열릴 때 초기 값으로 동기화 (isOpen: false -> true 일 때만)
  useEffect(() => {
    const wasJustOpened = isOpen && !prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (wasJustOpened) {
      setStep('body-select');
      setSelectedKeys(initialSelectedKeys);
      setNotesForKeys(initialNotesForKeys);
      setAnswerMap(initialAnswerMap);
      setRequireNoteKey(null);
      setRotate(false);
      setSelectedStack([]);
      // 로드한 question 키 추적 리셋 (새 세션 시작)
      loadedQuestionKeysRef.current.clear();
    }
  }, [isOpen, initialSelectedKeys, initialNotesForKeys, initialAnswerMap]);

  // Main option 클릭 핸들러
  const handleMainOptionClick = useCallback((optionKey: string) => {
    if (shouldBlockSelection) return;
    const option = mainOptions.find((o) => o.key === optionKey);
    const isSelected = selectedKeys.includes(optionKey);

    // 이미 선택된 경우 - 선택 해제
    if (isSelected) {
      setSelectedKeys((prev) => prev.filter((k) => k !== optionKey));
      setRequireNoteKey(null);
      return;
    }

    // requireNote가 true인 경우 (OTHER) - description 입력 모드로 전환
    if (option?.requireNote) {
      setRequireNoteKey(optionKey);
      return;
    }

    // 일반 선택 추가
    setSelectedKeys((prev) => [...prev, optionKey]);
  }, [selectedKeys, mainOptions, shouldBlockSelection]);

  // Description 입력 후 ADD 버튼 클릭 핸들러
  const handleAddWithNote = useCallback(() => {
    if (!requireNoteKey) return;

    setSelectedKeys((prev) => [...prev, requireNoteKey]);
    setRequireNoteKey(null);
  }, [requireNoteKey]);

  // Description 변경 핸들러
  const handleNoteChange = useCallback((key: string, value: string) => {
    setNotesForKeys((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // Subpart 번역 로드 함수 (V1의 PaQuestions API와 동일한 역할)
  const loadSubpartTranslations = useCallback(async (subpartKey: SubpartKey) => {
    // surveyId나 locale이 없으면 스킵
    if (!surveyId || !locale) return;

    // Question 키 가져오기
    const questionKey = optionToQuestionKeyMap.get(subpartKey);
    if (!questionKey) return;

    // 이미 로드한 경우 스킵
    if (loadedQuestionKeysRef.current.has(questionKey)) return;

    try {
      const response = await translationControllerTransForQuestion({
        surveyId: Number(surveyId),
        questionKey,
        locale,
        type: 'patient',
      });

      // API 응답에서 translations 추출
      const apiResponse = response as ApiResponse;
      const apiTranslations = apiResponse?.data?.translations || [];

      if (apiTranslations.length > 0) {
        // PaDataRepoState.translations 형식으로 변환
        const formattedTranslations = apiTranslations.map((tr) => ({
          id: tr.id?.toString() || null,
          key: tr.key,
          value: tr.value || null,
          locale: tr.locale || null,
          type: null,
          dataType: null,
        }));

        // PaDataRepoState에 translations 추가 (병합)
        setDataState((prev) => {
          const existingTranslations = prev.translations || [];
          const newTranslationsMap = new Map<string, (typeof prev.translations)[number]>();

          // 기존 translations 추가
          existingTranslations.forEach((tr) => {
            const uniqueKey = `${tr.key}-${tr.locale}`;
            newTranslationsMap.set(uniqueKey, tr);
          });

          // 새로운 translations 추가 (덮어쓰기)
          formattedTranslations.forEach((tr) => {
            const uniqueKey = `${tr.key}-${tr.locale}`;
            newTranslationsMap.set(uniqueKey, tr as (typeof prev.translations)[number]);
          });

          return {
            ...prev,
            translations: Array.from(newTranslationsMap.values()),
          };
        });

        // 로드 완료 표시
        loadedQuestionKeysRef.current.add(questionKey);
      }
    } catch (error) {
      console.error('Failed to load subpart translations:', error);
    }
  }, [surveyId, locale, setDataState, optionToQuestionKeyMap]);

  // SVG 클릭 핸들러
  const handleSvgClick = useCallback((e: React.MouseEvent) => {
    if (shouldBlockSelection) return;
    const el = e.target as HTMLElement;
    const id = el.getAttribute('id');
    if (!id) return;

    // 하위 화면이 있는 body part인 경우 - 네비게이션 + 번역 로드
    if (isSubpartKey(id)) {
      setSelectedStack((prev) => [...prev, id]);
      // Subpart 번역 로드 (V1처럼 on-demand로)
      loadSubpartTranslations(id);
      return;
    }

    // 일반 선택/해제
    setSelectedKeys((prev) => {
      if (prev.includes(id)) {
        return prev.filter((k) => k !== id);
      }
      return [...prev, id];
    });
  }, [loadSubpartTranslations, isSubpartKey, shouldBlockSelection]);

  // 뒤로가기 핸들러
  const handleBack = useCallback(() => {
    setSelectedStack((prev) => {
      const newStack = [...prev];
      newStack.pop();
      return newStack;
    });
  }, []);

  // 선택된 키 제거 핸들러
  const handleRemoveKey = useCallback((keyToRemove: string) => {
    setSelectedKeys((prev) => prev.filter((k) => k !== keyToRemove));
  }, []);

  // 저장 핸들러 (body-select 스텝에서 호출)
  const handleSave = useCallback(() => {
    if (selectedKeys.length === 0) return;
    onSave(selectedKeys, notesForKeys, answerMap);
  }, [onSave, selectedKeys, notesForKeys, answerMap]);

  const handleNext = useCallback(() => {
    if (selectedKeys.length === 0) return;
    if (hasFollowUps) {
      setSelectedStack([]);
      setStep('follow-ups');
      return;
    }
    handleSave();
  }, [selectedKeys, hasFollowUps, handleSave]);

  const handleBackFromFollowUps = useCallback(() => {
    setStep('body-select');
  }, []);

  const optionLabelByKey = useMemo(() => {
    const map = new Map<string, string>();

    const appendQuestionLabels = (question?: BodySelectTreeConfigResponseDto['entryQuestion']) => {
      if (!question) return;

      const answerLabels = (question.answerLabels || {}) as Record<string, string>;
      Object.entries(answerLabels).forEach(([key, label]) => {
        if (typeof label === 'string' && label.trim()) {
          map.set(key, label);
        }
      });

      const answerOptionsMap = (question.answerOptionsMap || {}) as Record<string, Record<string, unknown>>;
      Object.entries(answerOptionsMap).forEach(([key, option]) => {
        const label = option?.label;
        if (typeof label === 'string' && label.trim()) {
          map.set(key, label);
        }
      });
    };

    appendQuestionLabels(treeConfig?.entryQuestion);
    treeConfig?.subparts?.forEach((subpart) => appendQuestionLabels(subpart.question));
    followUpQuestions.forEach((question) => appendQuestionLabels(question as BodySelectTreeConfigResponseDto['entryQuestion']));

    return map;
  }, [treeConfig, followUpQuestions]);

  const subpartDisplayLabelByKey = useMemo(() => {
    const map = new Map<string, string>();

    treeConfig?.subparts?.forEach((subpart) => {
      const directionalLabelKey = getDirectionalLabelKey(subpart.optionKey);
      if (directionalLabelKey) {
        map.set(subpart.optionKey, sltStr({ key: directionalLabelKey, upper: true }));
        return;
      }

      const normalizedLabel = normalizeSubpartLabel(subpart?.question?.label);
      if (normalizedLabel) {
        map.set(subpart.optionKey, normalizedLabel);
      }
    });

    return map;
  }, [treeConfig, sltStr]);

  const resolvedTitle = useMemo(() => {
    if (typeof title === 'string') {
      return title;
    }
    const entryQuestionLabel = normalizeQuestionLabelForSurveyLocale({
      locale,
      label: treeConfig?.entryQuestion?.label,
    });
    if (entryQuestionLabel) {
      return entryQuestionLabel;
    }
    return upperCase(sltStr({ key: 'btn-bodypart' }));
  }, [locale, title, treeConfig?.entryQuestion?.label, sltStr]);

  // 라벨 가져오기 (V1과 동일하게 useTranslations 사용)
  const getLabel = useCallback((key: string) => {
    const translated = t(key);
    if (translated && translated !== key) {
      return translated;
    }

    const optionLabel = optionLabelByKey.get(key);
    if (optionLabel) {
      return optionLabel;
    }

    return translated || key;
  }, [optionLabelByKey, t]);

  const getSubpartLabel = useCallback((key: string) => {
    const subpartLabel = subpartDisplayLabelByKey.get(key);
    if (subpartLabel) {
      return subpartLabel;
    }
    return getLabel(key);
  }, [subpartDisplayLabelByKey, getLabel]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" data-theme="patientLight">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative w-full max-w-lg bg-white rounded-t-xl max-h-[90vh] flex flex-col animate-slide-up">
        {/* Handle */}
        <div className="flex justify-center pt-3">
          <div className="w-10 h-1.5 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div className="flex-1 flex flex-col items-center">
            <h3 className="text-base font-medium text-[#222638]">
              {resolvedTitle}
            </h3>
            <p className="text-sm text-gray-500">
              {subtitle || sltStr({ key: 'label-best-guess' })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors absolute right-2 top-3"
          >
            <MdClose size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 pb-4">
          {/* === Body Select Step === */}
          {/* eslint-disable-next-line no-nested-ternary */}
          {showMissingTree ? (
            <div className="py-6 text-center text-sm text-gray-500">
              {sltStr({ key: 'common-data-not-found' })}
            </div>
          ) : showLoading ? (
            <div className="py-6 text-center text-sm text-gray-500">
              {sltStr({ key: 'body-select-loading-parts' })}
            </div>
          ) : (
            <>
              {/* === 하위 화면 (Subpart View) === */}
              {currentSubpart ? (
                <>
                  {/* 뒤로가기 버튼 */}
                  <div className="py-4">
                    <button
                      type="button"
                      className="flex items-center gap-3 cursor-pointer"
                      onClick={handleBack}
                    >
                      <FaArrowLeft size={13} />
                      <span className="opacity-80 uppercase">{getSubpartLabel(currentSubpart)}</span>
                    </button>
                  </div>

              {/* Subpart Main Options (V1과 동일하게 표시) */}
              {subpartMainOptionsMap[currentSubpart] && subpartMainOptionsMap[currentSubpart].length > 0 && (
                <div className="flex gap-2 flex-wrap pb-4">
                  {subpartMainOptionsMap[currentSubpart].map((option) => {
                    const isSelected = selectedKeys.includes(option.key);

                    return (
                      <button
                        key={option.key}
                        type="button"
                        className="flex-1 btn-base"
                        onClick={() => {
                          // 이미 선택된 경우 - 선택 해제
                          if (isSelected) {
                            setSelectedKeys((prev) => prev.filter((k) => k !== option.key));
                            setRequireNoteKey(null);
                            return;
                          }

                          // requireNote인 경우 - description 입력 모드로 전환
                          if (option.requireNote) {
                            setRequireNoteKey(option.key);
                            return;
                          }

                          // 일반 선택 추가
                          setSelectedKeys((prev) => [...prev, option.key]);
                        }}
                      >
                        {isSelected ? (
                          <BsPlusCircle size={22} className="text-primary rotate-45" />
                        ) : (
                          <BsFillPlusCircleFill size={22} className="text-primary" />
                        )}
                        <span className="text-base text-primary">{upperCase(getLabel(option.key))}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Description 입력 필드 (OTHER 클릭 시 표시 - subpart용) */}
              {requireNoteKey && (
                <div className="flex-none flex pb-3 gap-2">
                  <TextField
                    fullWidth
                    label={sltStr({ key: 'label-area-description', upper: true })}
                    value={notesForKeys[requireNoteKey] || ''}
                    onChange={(e) => handleNoteChange(requireNoteKey, e.target.value)}
                    autoComplete="off"
                    autoFocus
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            variant="contained"
                            disabled={!isNotEmpty(notesForKeys[requireNoteKey])}
                            onClick={handleAddWithNote}
                            color="primary"
                          >
                            {sltStr({ key: 'btn-add' })}
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              )}

              {/* Subpart SVG */}
              <div className="relative flex justify-center pt-8">
                {/* LEFT/RIGHT Labels */}
                {['o-abdomen', 'o-chest', 'o-bottom', 'o-face', 'o-mouth'].includes(currentSubpart) && (
                  <div className="absolute flex-center top-0 w-full flex justify-between opacity-60 pointer-events-none">
                    <span className="p-5 text-sm">{sltStr({ key: 'label-right', upper: true })}</span>
                    <span className="p-5 text-sm">{sltStr({ key: 'label-left', upper: true })}</span>
                  </div>
                )}
                {['o-back-upper', 'o-back-lower'].includes(currentSubpart) && (
                  <div className="absolute flex-center top-0 w-full flex justify-between opacity-60 pointer-events-none">
                    <span className="p-5 text-sm">{sltStr({ key: 'label-left', upper: true })}</span>
                    <span className="p-5 text-sm">{sltStr({ key: 'label-right', upper: true })}</span>
                  </div>
                )}

                {/* Chest */}
                {currentSubpart === 'o-chest' && isMale && (
                  <WholeChestMaleButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}
                {currentSubpart === 'o-chest' && !isMale && (
                  <WholeChestFemaleButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}

                {/* Face */}
                {currentSubpart === 'o-face' && isMale && (
                  <FaceMaleButtons
                    selectedKeys={selectedKeys}
                    subQuestionKeys={['o-mouth']}
                    className="relative"
                    onClick={handleSvgClick}
                  />
                )}
                {currentSubpart === 'o-face' && !isMale && (
                  <FaceFemaleButtons
                    selectedKeys={selectedKeys}
                    subQuestionKeys={['o-mouth']}
                    className="relative"
                    onClick={handleSvgClick}
                  />
                )}

                {/* Mouth */}
                {currentSubpart === 'o-mouth' && (
                  <div className="flex flex-col items-center">
                    {tongueLift ? (
                      <MouthLiftButtons
                        selectedKeys={selectedKeys}
                        className="relative"
                        onClick={handleSvgClick}
                      />
                    ) : (
                      <MouthButtons
                        selectedKeys={selectedKeys}
                        className="relative"
                        onClick={handleSvgClick}
                      />
                    )}
                    {/* TONGUE LIFT 토글 버튼 */}
                    <div className="flex-center py-4">
                      <button
                        type="button"
                        className="btn"
                        onClick={() => setTongueLift(!tongueLift)}
                      >
                        <FaArrowsUpDown /> {sltStr({ key: 'btn-lift-tongue', upper: true })}
                      </button>
                    </div>
                  </div>
                )}

                {/* Abdomen */}
                {currentSubpart === 'o-abdomen' && isMale && (
                  <BellyMaleButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}
                {currentSubpart === 'o-abdomen' && !isMale && (
                  <BellyFemaleButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}

                {/* Bottom */}
                {currentSubpart === 'o-bottom' && isMale && (
                  <BottomMaleButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}
                {currentSubpart === 'o-bottom' && !isMale && (
                  <BottomFemaleButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}

                {/* Back Upper */}
                {currentSubpart === 'o-back-upper' && isMale && (
                  <WholeBodyBackUpperMaleButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}
                {currentSubpart === 'o-back-upper' && !isMale && (
                  <BackUpperFemaleButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}

                {/* Back Lower */}
                {currentSubpart === 'o-back-lower' && isMale && (
                  <WholeBodyBackLowerMaleButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}
                {currentSubpart === 'o-back-lower' && !isMale && (
                  <BackLowerFemaleButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}

                {/* Right Arm */}
                {currentSubpart === 'o-arm-rt' && isMale && (
                  <RightArmMaleButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}
                {currentSubpart === 'o-arm-rt' && !isMale && (
                  <RightArmFemaleButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}

                {/* Left Arm */}
                {currentSubpart === 'o-arm-lt' && isMale && (
                  <LeftArmMaleButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}
                {currentSubpart === 'o-arm-lt' && !isMale && (
                  <LeftArmFemaleButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}

                {/* Right Hand */}
                {currentSubpart === 'o-hand-rt' && (
                  <HandRightButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}

                {/* Left Hand */}
                {currentSubpart === 'o-hand-lt' && (
                  <HandLeftButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}

                {/* Right Leg */}
                {currentSubpart === 'o-leg-rt' && isMale && (
                  <LegRightButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}
                {currentSubpart === 'o-leg-rt' && !isMale && (
                  <LegRightFemaleButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}

                {/* Left Leg */}
                {currentSubpart === 'o-leg-lt' && isMale && (
                  <LegLeftButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}
                {currentSubpart === 'o-leg-lt' && !isMale && (
                  <LegLeftFemaleButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}

                {/* Right Foot */}
                {currentSubpart === 'o-foot-rt' && (
                  <FootRightButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}

                {/* Left Foot */}
                {currentSubpart === 'o-foot-lt' && (
                  <FootLeftButtons selectedKeys={selectedKeys} className="relative" onClick={handleSvgClick} />
                )}

                {/* Head/Neck - 체크박스 리스트 (SVG 대신) */}
                {currentSubpart && CHECKBOX_SUBPART_KEYS.has(currentSubpart) && subpartCheckboxOptionsMap[currentSubpart] && (
                  <div className="flex flex-col gap-3 w-full">
                    {subpartCheckboxOptionsMap[currentSubpart]?.map((option) => {
                      const isChecked = selectedKeys.includes(option.key);
                      const showNoteInput = isChecked && option.requireNote;
                      const note = notesForKeys[option.key] || '';

                      return (
                        <div key={option.key}>
                          <label className="cursor-pointer border border-gray-200 p-3 rounded-xl flex flex-col gap-3 hover:bg-gray-50 transition-colors">
                            <div className="flex-1 w-full flex flex-row items-center gap-4">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                className="checkbox checkbox-lg checkbox-primary"
                                onChange={(e) => {
                                  const { checked } = e.target;
                                  if (checked) {
                                    setSelectedKeys((prev) => [...prev, option.key]);
                                  } else {
                                    setSelectedKeys((prev) => prev.filter((k) => k !== option.key));
                                    // 체크 해제 시 note도 삭제
                                    setNotesForKeys((prev) => {
                                      const newNotes = { ...prev };
                                      delete newNotes[option.key];
                                      return newNotes;
                                    });
                                  }
                                }}
                              />
                              <span className="pt-[0.1rem]">{getLabel(option.key)}</span>
                            </div>

                            {/* requireNote인 경우 description 입력 필드 */}
                            {showNoteInput && (
                              <input
                                type="text"
                                placeholder={sltStr({ key: 'label-describe-in-detail', upper: true })}
                                value={note}
                                className="input w-full border border-gray-200 rounded-xl"
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  handleNoteChange(option.key, e.target.value);
                                }}
                              />
                            )}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Selected Parts (V1 parity: always visible) */}
              {selectedKeys.length > 0 && (
                <div className="flex flex-wrap gap-3 pt-4 mt-4 border-t">
                  {selectedKeys.map((key) => {
                    const note = notesForKeys[key];
                    return (
                      <button
                        key={key}
                        type="button"
                        className="btn btn-outline btn-primary border-primary p-2 min-h-9 max-w-full flex gap-2"
                        onClick={() => handleRemoveKey(key)}
                      >
                        <span className="uppercase">{getLabel(key)}</span>
                        {note ? ` (${truncate(note, { length: 75 })})` : null}
                        <MdClose size={16} />
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              {/* === 메인 화면 (Main Body View) === */}
              {step === 'body-select' && (
                <>
                  {/* Main Options (WHOLE BODY, OTHER) - V1 스타일 */}
                  <div className="flex gap-2 flex-wrap py-4">
                    {mainOptions.map((option) => {
                      const isSelected = selectedKeys.includes(option.key);
                      return (
                        <button
                          key={option.key}
                          type="button"
                          className="flex-1 btn-base"
                          onClick={() => handleMainOptionClick(option.key)}
                        >
                          {isSelected ? (
                            <BsPlusCircle size={22} className="text-primary rotate-45" />
                          ) : (
                            <BsFillPlusCircleFill size={22} className="text-primary" />
                          )}
                          <span className="text-base text-primary">{upperCase(getLabel(option.key))}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Description 입력 필드 (OTHER 클릭 시 표시) */}
                  {requireNoteKey && (
                    <div className="flex-none flex pb-3 gap-2">
                      <TextField
                        fullWidth
                        label={sltStr({ key: 'label-area-description', upper: true })}
                        value={notesForKeys[requireNoteKey] || ''}
                        onChange={(e) => handleNoteChange(requireNoteKey, e.target.value)}
                        autoComplete="off"
                        autoFocus
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button
                                variant="contained"
                                disabled={!isNotEmpty(notesForKeys[requireNoteKey])}
                                onClick={handleAddWithNote}
                                color="primary"
                              >
                                {sltStr({ key: 'btn-add' })}
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </div>
                  )}

                  {/* LEFT/RIGHT Labels + Body SVG */}
                  <div className="relative flex justify-center pt-8">
                    <div className="absolute flex-center top-0 w-full flex justify-between opacity-60 pointer-events-none">
                      <span className="p-5 text-sm">{sltStr({ key: 'label-right', upper: true })}</span>
                      <span className="p-5 text-sm">{sltStr({ key: 'label-left', upper: true })}</span>
                    </div>

                    {/* Body SVG - Front/Back toggle */}
                    {isMale && rotate && (
                      <BodyRotateMaleButtons
                        selectedKeys={selectedKeys}
                        subQuestionKeys={subpartKeys}
                        className="relative max-w-full"
                        onClick={handleSvgClick}
                      />
                    )}
                    {isMale && !rotate && (
                      <BodyMaleButtons
                        selectedKeys={selectedKeys}
                        subQuestionKeys={subpartKeys}
                        className="relative max-w-full"
                        onClick={handleSvgClick}
                      />
                    )}
                    {!isMale && rotate && (
                      <BackFemaleButtons
                        selectedKeys={selectedKeys}
                        subQuestionKeys={subpartKeys}
                        className="relative max-w-full"
                        onClick={handleSvgClick}
                      />
                    )}
                    {!isMale && !rotate && (
                      <BodyFemaleButtons
                        selectedKeys={selectedKeys}
                        subQuestionKeys={subpartKeys}
                        className="relative max-w-full"
                        onClick={handleSvgClick}
                      />
                    )}
                  </div>

                  {/* ROTATE 버튼 */}
                  <div className="flex-center pt-4">
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setRotate(!rotate)}
                    >
                      <FaRotate /> {sltStr({ key: 'btn-rotate', upper: true })}
                    </button>
                  </div>

                  {/* Selected Parts */}
                  {selectedKeys.length > 0 && (
                    <div className="flex flex-wrap gap-3 pt-4 mt-4 border-t">
                      {selectedKeys.map((key) => {
                        const note = notesForKeys[key];
                        return (
                          <button
                            key={key}
                            type="button"
                            className="btn btn-outline btn-primary border-primary p-2 min-h-9 max-w-full flex gap-2"
                            onClick={() => handleRemoveKey(key)}
                          >
                            <span className="uppercase">{getLabel(key)}</span>
                            {note ? ` (${truncate(note, { length: 75 })})` : null}
                            <MdClose size={16} />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {step === 'follow-ups' && followUpQuestions.length > 0 && (
                <div className="pt-6 flex flex-col gap-5">
                  <button
                    type="button"
                    className="flex items-center gap-3 cursor-pointer text-[#737580]"
                    onClick={handleBackFromFollowUps}
                  >
                    <FaArrowLeft size={13} />
                    <span className="opacity-80 uppercase">Back</span>
                  </button>
                  {followUpQuestions.map((question) => {
                    const label = normalizeQuestionLabelForSurveyLocale({
                      locale,
                      label: question.label,
                    });
                    const value = answerMap[question.questionKey];
                    return (
                      <div key={question.questionKey} className="flex flex-col gap-2">
                        <div className="flex items-start gap-2">
                          <span className="text-[15px] font-medium text-[#222638] leading-relaxed">
                            {label}
                            {!question.optional && ' *'}
                          </span>
                          {question.optional && (
                            <span className="text-[13px] text-[#737580]">
                              ({sltStr({ key: 'optional' })})
                            </span>
                          )}
                        </div>
                        {question.descriptionInline && (
                          <p className="text-[13px] text-[#737580] leading-relaxed">
                            {question.descriptionInline}
                          </p>
                        )}
                        <LiveTestingQuestionFieldInner
                          question={question}
                          value={(value as LiveTestingAnswerValue) || ''}
                          onChange={(nextValue) => {
                            setAnswerMap((prev) => ({
                              ...prev,
                              [question.questionKey]: nextValue,
                            }));
                          }}
                          locale={locale}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex-none px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={step === 'body-select' ? handleNext : handleSave}
            disabled={!canProceed || selectedKeys.length === 0}
            className="btn btn-primary w-full btn-lg disabled:opacity-50"
          >
            {step === 'body-select' && hasFollowUps
              ? sltStr({ key: 'btn-next', capit: true })
              : (saveButtonText || sltStr({ key: 'btn-save', capit: true }))}
          </button>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default memo(LiveTestingBodySelectTreeModal);
